<template>
  <div class="edit-id bg-background" :class="{ invisible: isCameraActive }">
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      身分証明書
    </Header>

    <RejectionReason
      v-if="isRejected"
      :details="rejectionReasonDetails"
      :comment="rejectionReasonComment"
    />

    <div class="py-4 mx-3">
      <CustomValidationObserver
        v-slot="{ handleSubmit }"
        :initialValues="formValues"
      >
        <SetId
          v-model="inputValues"
          v-model:isCameraActive="isCameraActive"
          :disabled="disabledForm"
          :disabledText="disabledText"
          :isVerified="isVerified"
        />
        <AppButton
          block
          class="mt-5"
          @click="handleSubmit(onClick)"
          :disabled="disabledSubmitButton"
        >
          {{ buttonLabel }}
        </AppButton>
      </CustomValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { accountService } from "@/app/services/AccountService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import RejectionReason from "@/components/atoms/other/RejectionReason.vue";
import Header from "@/components/organisms/common/Header.vue";
import SetId from "@/components/organisms/register/SetId.vue";
import VerifiedStatus from "@/models/verifiedStatus";
import { authStore } from "@/store/auth";
import {
  IdentifierInputValues,
  RequestVerificationQueueBody
} from "@/types/user/RequestVerificationQueueBody";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Component, Vue } from "vue-facing-decorator";

type IdentifierInputValuesKey = keyof IdentifierInputValues;

@Component({
  components: {
    Header,
    AppButton,
    FeatherIcon,
    SetId,
    RejectionReason
  }
})
export default class MypageEditId extends Vue {
  inputValues: IdentifierInputValues = {
    identificationImage: null,
    identificationBackImage: null,
    identificationType: null
  };

  // 項目ごとの変更監視のためのデータプロパティ
  originalInputValues: IdentifierInputValues = {
    identificationImage: null,
    identificationBackImage: null,
    identificationType: null
  };

  isCameraActive = false;
  rejectionReasonComment: string | null = null;
  requestQueueUpdatedAt: string | null = null;

  get formValues() {
    return {
      identificationType: this.inputValues.identificationType
    };
  }

  get initialUploadImage() {
    return this.user.identificationImage;
  }

  get initialUploadBackImage() {
    return this.user.identificationBackImage;
  }

  get isUnverified() {
    return this.user.verifiedStatus === VerifiedStatus.UNVERIFIED;
  }

  get isVerified() {
    return this.user.verifiedStatus === VerifiedStatus.VERIFIED;
  }

  get isRejected() {
    return (
      this.user.verifiedStatus === VerifiedStatus.REJECTED &&
      this.user.rejectionReason.title === "身分証画像不正"
    );
  }

  get rejectionReasonDetails() {
    return this.user.rejectionReason.details;
  }

  get isNotUploaded() {
    return (
      this.initialUploadImage === null && this.initialUploadBackImage === null
    );
  }

  get isEditable() {
    // 未登録時・承認時・却下時のみ編集可能
    // （審査中の場合は編集不可）
    return (
      this.isVerified ||
      this.isRejected ||
      this.isNotUploaded ||
      !this.hasRegisteredIdentifier ||
      !this.hasRegisteredProfile
    );
  }

  get user() {
    return authStore.user!;
  }

  get isUnderExamination() {
    if (!this.hasRegisteredIdentifier) return false;
    if (!this.hasRegisteredProfile) return false;
    if (this.user.verifiedStatus === VerifiedStatus.UNVERIFIED) {
      return true;
    }
    // 編集不可状態であれば審査中とみなす
    return !this.isEditable;
  }

  get hasRegisteredIdentifier() {
    return authStore.hasRegisteredIdentifier;
  }

  get hasRegisteredProfile() {
    return this.user.hasRegisteredProfile;
  }

  get disabledText() {
    if (this.isUnderExamination) return "審査中は変更できません";
    return "";
  }

  get buttonLabel() {
    if (this.isUnderExamination) return "審査中は変更できません";
    return "保存する";
  }

  get disabledForm() {
    return this.isUnderExamination || !this.isEditable;
  }

  get disabledSubmitButton() {
    return this.disabledForm || !this.isDirty;
  }

  get isDirty() {
    // 初回認証未送信の場合は常に true
    if (!this.alreadyRequestedVerification) {
      return true;
    }
    // 上記以外は元の値から変更があれば true
    return Object.entries(this.originalInputValues).some(
      ([k, v]) => this.inputValues[k as IdentifierInputValuesKey] !== v
    );
  }

  get alreadyRequestedVerification() {
    return this.hasRegisteredIdentifier && this.hasRegisteredProfile;
  }

  get requestIdentificationImage() {
    return this.getImageValueForRequest(this.inputValues.identificationImage);
  }

  get requestIdentificationBackImage() {
    // マイナンバーカードの場合、裏面画像を明示的にクリアするため null を返す
    if (this.inputValues.identificationType === "my_number_card") {
      return null;
    }
    return this.getImageValueForRequest(
      this.inputValues.identificationBackImage
    );
  }

  async created() {
    try {
      this.$loading.show();
      await this.initInput();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async onClick() {
    if (this.inputValues.identificationType === "citizen_identification_card") {
      this.$bvModal.msgBoxOk(
        "住民基本台帳カードでの新規の身分証明書登録はできません。他の身分証明書での登録をお願いします。"
      );
      return;
    }
    if (
      !this.inputValues.identificationImage &&
      !this.inputValues.identificationBackImage
    ) {
      this.$bvModal.msgBoxOk(
        "身分証明書を撮影、またはファイル選択してください。",
        {
          centered: true
        }
      );
      return;
    }
    if (
      !this.inputValues.identificationImage &&
      this.inputValues.identificationBackImage
    ) {
      this.$bvModal.msgBoxOk(
        "裏面の写真のみで保存することはできません。表面の身分証明書を撮影、またはファイル選択してください。",
        {
          centered: true
        }
      );
      return;
    }
    this.$loading.show();
    try {
      // 画像については、リクエストに含める場合は base64 形式のものである必要がある
      // しかし、データ取得時は url 形式で返ってくるため、変更無しの場合はそれらをリクエストに含めないようにする必要がある
      const body = {
        identificationType: this.inputValues.identificationType!,
        hasRegisteredIdentifier: true,
        updatedAt: this.requestQueueUpdatedAt
      } as Partial<RequestVerificationQueueBody>;
      if (this.requestIdentificationImage !== undefined) {
        body.identificationImage = this.requestIdentificationImage;
      }
      if (this.requestIdentificationBackImage !== undefined) {
        body.identificationBackImage = this.requestIdentificationBackImage;
      }
      if (this.alreadyRequestedVerification) {
        // 初回登録済みのユーザーの場合requestQueueを作成or更新
        await accountService.requestVerificationQueue(body);
      } else {
        // 初回登録がまだのユーザーの場合userDetailを更新
        // リクエスト内にフィールドが存在するとその値で更新しようとするため、未変更だった項目がエラーになってしまう
        // 未変更だった項目については削除しておく
        // 画像については削除アクションが無いため、
        await authStore.registerUserInfo(body);
      }
      await this.$router.push({ name: "RegistrationInformationAndStatus" });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async initInput() {
    const latestRequestVerificationQueue = await accountService.fetchLatestRequestVerificationQueue();
    this.rejectionReasonComment =
      latestRequestVerificationQueue?.comment || null;
    this.requestQueueUpdatedAt = latestRequestVerificationQueue?.updatedAt;
    this.setInputValues(latestRequestVerificationQueue);
    // ここで初期値を保存しておく
    this.originalInputValues = { ...this.inputValues };
  }

  getImageValueForRequest(value: string | null) {
    // リクエストに含める画像情報を返す
    // ユーザーがファイルを選択し、一度もリクエストを送っていない場合は data 形式の値が設定されている
    // この場合はリクエストに含めたいので値をそのまま返す
    if (/^data:*/.test(value || "")) {
      return value;
    }
    // ファイルの選択をクリアした場合（未選択含む）は null を返す
    // ファイルを変更していない場合はリクエストに含めたくないため null ではなく undefined を返す
    return value ? undefined : null;
  }

  setInputValues(queue: RequestVerificationQueueBody | null) {
    // requestVerificationQueue に値があればその値を、なければ user の値を利用する
    // 身分証明書 が登録されてない場合は ユーザー情報 には空文字が入力されているので null を返す
    const inputValues = Object.fromEntries(
      Object.keys(this.inputValues).map(k => {
        const key = k as keyof typeof this.inputValues;
        const value =
          queue && queue[key] !== null ? queue[key] : this.user[key] || null;
        return [key, value];
      })
    ) as IdentifierInputValues;
    // 裏面画像については任意項目なので、キュー側の値が null になり得る
    // その場合、キューとユーザー情報で身分証種別が異なる場合のみ変更ありとみなし、常にキュー側の値を設定する
    // 初回登録の場合は何もしない
    // 身分証以外を更新した場合、キューには身分証種別が設定されないので、その場合も何もしない
    if (
      this.alreadyRequestedVerification &&
      queue?.identificationType !== null &&
      this.user.identificationType !== queue?.identificationType
    ) {
      inputValues.identificationBackImage =
        queue?.identificationBackImage || null;
    }
    this.inputValues = inputValues;
  }
}
</script>

<style lang="scss" scoped>
.edit-id {
  min-height: inherit;
}
</style>
